import { Box, Stack, Typography } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import {
  RichTextInput,
  RichTextInputToolbar,
  FormatButtons,
  ColorButtons,
  AlignmentButtons,
  ListButtons,
  LinkButtons,
  QuoteButtons,
  ClearButtons,
} from "ra-input-rich-text";
import { SimpleForm } from "react-admin";
import { useFormContext } from "react-hook-form";
import { useEffect, useCallback } from "react";
import Button from "@/components/ui/Button";
import { ButtonIconLoadingPosition } from "@/types/shared/uiComponents";

interface CommentInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  /** Tighter footprint (shorter editor, slimmer footer) for dense surfaces like the comments drawer. */
  compact?: boolean;
}

// Inner component to access form context
const RichTextInputWithWatcher = ({
  value,
  onChange,
  onSubmit,
  disabled,
  compact,
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  disabled: boolean;
  compact?: boolean;
}) => {
  const { watch } = useFormContext();
  const content = watch("content");

  useEffect(() => {
    if (content !== undefined) {
      onChange(content);
    }
  }, [content, onChange]);

  const hasContent = value && value !== "<p></p>" && value.trim().length > 7;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Cmd/Ctrl+Enter submits. Plain Enter is left to the editor so it behaves
      // natively — new paragraph in prose, continue/exit a bullet or numbered list —
      // letting a comment mix sentences and lists freely. Shift+Enter = soft line break.
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && hasContent && !disabled) {
        e.preventDefault();
        onSubmit();
      }
    },
    [hasContent, disabled, onSubmit]
  );

  return (
    <Box onKeyDown={handleKeyDown} sx={{ width: "100%" }}>
      <RichTextInput
        source="content"
        label={false}
        fullWidth
        disabled={disabled}
        toolbar={
          // Default toolbar minus ImageButtons — comments don't need image-by-URL,
          // and the prompt-for-URL dialog is awkward. Everything else (formatting,
          // lists, link, quote, clear) is kept.
          <RichTextInputToolbar size="small">
            <FormatButtons size="small" />
            <ColorButtons size="small" />
            <AlignmentButtons size="small" />
            <ListButtons size="small" />
            <LinkButtons size="small" />
            <QuoteButtons size="small" />
            <ClearButtons size="small" />
          </RichTextInputToolbar>
        }
        sx={{
          m: 0,
          width: "100%",
          "& .RaRichTextInputToolbar-root": {
            width: "100%",
            px: 1,
            py: 0.5,
            borderBottom: 1,
            borderColor: "divider",
          },
          "& .RaRichTextInput-editorContent": {
            border: "none",
            bgcolor: "transparent",
            boxShadow: "none",
            m: 0,
            pb: 0,
            width: "100%",
          },
          "& .tiptap.ProseMirror": {
            border: "none",
            outline: "none",
            boxShadow: "none",
            bgcolor: "transparent",
            minHeight: compact ? 36 : 60,
            px: 1.5,
            py: compact ? 0.5 : 1,
            mb: 0,
            wordBreak: "break-word",
            whiteSpace: "pre-wrap",
            overflowWrap: "break-word",
            "&:focus": {
              outline: "none",
              boxShadow: "none",
            },
          },
        }}
      />
    </Box>
  );
};

export const CommentInput = ({ value, onChange, onSubmit, disabled = false, compact = false }: CommentInputProps) => {
  const hasContent = value && value !== "<p></p>" && value.trim().length > 7;

  return (
    <Box
      sx={{
        mt: compact ? 1 : 2,
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
        "&:focus-within": {
          borderColor: "primary.main",
        },
      }}
    >
      <SimpleForm
        toolbar={false}
        record={{ content: value }}
        onSubmit={() => {}}
        sx={{
          "& .RaSimpleForm-main": {
            padding: 0,
            margin: 0,
          },
          "& .ra-input": {
            margin: 0,
          },
        }}
      >
        <RichTextInputWithWatcher
          value={value}
          onChange={onChange}
          onSubmit={onSubmit}
          disabled={disabled}
          compact={compact}
        />
      </SimpleForm>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          px: 1.5,
          py: compact ? 0.5 : 1,
          bgcolor: "action.hover",
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          ⌘/Ctrl+Enter to send
        </Typography>
        <Button
          onClick={onSubmit}
          disabled={!hasContent || disabled}
          loading={disabled}
          startIcon={<SendIcon />}
          loadingPosition={ButtonIconLoadingPosition.Start}
          size="small"
        >
          {disabled ? "Sending..." : "Send"}
        </Button>
      </Stack>
    </Box>
  );
};
